"use client";
import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import logo from "../../assets/Common/logo12.svg";
import dropdown from "../../assets/Common/dropdown.svg";
import cross from "../../assets/Common/cross.svg";
import star from "../../assets/Common/star.svg";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import { useScreen } from "../../context/ScreenContext";
import { ChevronDown, ArrowRight } from "lucide-react";
import AnimatedButton from "./AnimatedButton";
import axios from "axios";

export default function Navbar() {
  const navigate = useNavigate();

  const [isOfferingsOpen, setIsOfferingsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { isMobile } = useScreen();
  const API_BASE = import.meta.env.VITE_API_BASE_URL;

  const sidebarRef = useRef(null);
  const buttonRef = useRef(null);

  useOnClickOutside(
    [sidebarRef, buttonRef],
    () => {
      setIsSidebarOpen(false);
      setIsOfferingsOpen(false);
    },
    isMobile
  );

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const [bannerOpen, setBannerOpen] = useState(true);
  const [index, setIndex] = useState(0);
  const [banners, setBanners] = useState([
    {
      key: "subscribe",
      type: "button",
      onClick: () => navigate("/#newsletter"),
      content: [
        { text: "Stay ahead - ", className: "hidden md:block" },
        { text: "Subscribe to Brain Gain", className: "font-semibold" },
        { text: "for insights and updates.", className: "hidden md:block" },
      ],
    },
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % banners.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [banners.length]); // <-- Make sure it updates when banners change

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const res = await axios.get(`${API_BASE}getBanner`);
        const data = res.data;
        setBanners((prev) => [
          ...prev,
          {
            key: "dynamic-banner",
            type: "link",
            href: data.link,
            content: [
              {
                text: data.text,
                className: `${data.bold ? "font-semibold" : "font-normal"} ${
                  data.italic ? "italic" : ""
                } ${data.underline ? "underline" : ""}`,
              },
            ],
          },
        ]);
      } catch (err) {
        console.error("Failed to load banner", err);
      }
    };

    fetchBanner();
  }, []);

  //   <a
  //     key="dynamic-banner"
  //     className="flex md:flex-row flex-col items-center justify-center md:gap-[4px]"
  //     href={banner.link}
  //     target="_blank"
  //   >
  //     <p
  //       className={`${banner.bold ? "font-semibold" : "font-normal"
  //         } ${banner.italic ? "italic" : ""} ${banner.underline ? "underline" : ""}`}
  //     >
  //       {banner.text}
  //     </p>
  //   </a>,

  // <button
  //   key="subscribe"
  //   onClick={() => navigate("/#newsletter")}
  //   className="flex gap-[4px] cursor-pointer"
  // >
  //   <p className="hidden md:block">Stay ahead - </p>
  //   <p className="font-semibold">Subscribe to Brain Gain</p>
  //   <p className="hidden md:block">for insights and updates.</p>
  // </button>

  return (
    <div className="w-full flex flex-col items-center justify-center sticky top-0 z-[999]">
      {bannerOpen && banners.length > 0 && (
        <div className="min-h-[52px] w-full flex justify-between items-center px-[20px] bg-t-blue-1 font-montserrat text-white">
          <div className="text-center w-full flex md:flex-row flex-col justify-center items-center md:gap-[4px]">
            {(() => {
              const b = banners[index];
              if (b.type === "button") {
                return (
                  <button
                    key={b.key}
                    onClick={b.onClick}
                    className="flex gap-[4px] cursor-pointer"
                  >
                    {b.content.map((p, i) => (
                      <p key={i} className={p.className}>
                        {p.text}
                      </p>
                    ))}
                  </button>
                );
              }
              if (b.type === "link") {
                return (
                  <a
                    key={b.key}
                    href={b.href}
                    target="_blank"
                    className="flex md:flex-row flex-col items-center justify-center md:gap-[4px]"
                  >
                    {b.content.map((p, i) => (
                      <p key={i} className={p.className}>
                        {p.text}
                      </p>
                    ))}
                  </a>
                );
              }
              return null;
            })()}
          </div>
          <p
            className="font-bold cursor-pointer"
            onClick={() => setBannerOpen(false)}
          >
            X
          </p>
        </div>
      )}
      <header
        className={`transition-all duration-500 ease-in-out py-4
    ${
      scrolled
        ? "bg-white/30 backdrop-blur-sm shadow-xl drop-shadow-[0_4px_10px_rgba(0,0,0,0.05)] pb-4 px-6 md:px-10 rounded-full w-full max-w-[1220px] mx-auto translate-y-2"
        : "bg-white py-4 px-[20px] w-full max-w-full mx-auto"
    }
  `}
        style={{ transitionProperty: "all" }}
      >
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center">
            <img
              src={logo}
              alt="Think41 Logo"
              className="h-8 w-auto hidden md:block"
            />
            <img
              src={logo}
              alt="Think41 Mobile Logo"
              className="h-8 w-auto block md:hidden"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-baseline space-x-8 font-montserrat text-[16px] font-[600] ml-8">
            <Link to="/about" className="relative group text-black">
              About Us
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-500 transition-all duration-300 group-hover:w-full" />
            </Link>

            <div
              className="relative"
              onMouseLeave={() => setIsOfferingsOpen(false)}
            >
              <button
                className="flex items-center relative group text-black"
                onClick={() => setIsOfferingsOpen(!isOfferingsOpen)}
                onMouseEnter={() => setIsOfferingsOpen(true)}
              >
                Offerings
                <ChevronDown className="ml-1 h-4 w-4" />
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-500 transition-all duration-300 group-hover:w-full" />
              </button>

              <div
                className={`absolute top-full left-0 bg-white rounded shadow-lg py-2 w-64 z-20 transition-opacity duration-200 ${
                  isOfferingsOpen
                    ? "opacity-100"
                    : "opacity-0 pointer-events-none"
                }`}
              >
                <Link
                  to="/offering-a"
                  className="flex items-center justify-between px-4 py-3 text-[16px] font-roboto font-[400] text-gray-800 hover:bg-gray-100 group/item"
                >
                  AI Solutions
                  <ArrowRight className="h-4 w-4 text-gray-500 group-hover/item:rotate-[-45deg] transition-transform duration-300" />
                </Link>

                <Link
                  to="/offering-a#ai_platform"
                  className="flex items-center justify-between px-4 py-3 text-[16px] font-roboto font-[400] text-gray-800 hover:bg-gray-100 group/item "
                >
                  AI Platforms
                  <ArrowRight className="h-4 w-4 text-gray-500 transition-transform duration-300 group-hover/item:rotate-[-45deg]" />
                </Link>
                <Link
                  to="/offering-b#ai_engineering"
                  className="flex items-center justify-between px-4 py-3 text-[16px] font-roboto font-[400] text-gray-800 hover:bg-gray-100 group/item "
                >
                  AI Engineering
                  <ArrowRight className="h-4 w-4 text-gray-500 group-hover/item:rotate-[-45deg] transition-transform duration-300" />
                </Link>
              </div>
            </div>

            <Link to="/careers" className="relative group text-black">
              Careers
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-500 transition-all duration-300 group-hover:w-full" />
            </Link>
            <Link to="/contact" className="relative group text-black">
              Contact Us
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-500 transition-all duration-300 group-hover:w-full" />
            </Link>

            {/* <a
              href="https://ai.think41.com/login"
              target="_blank"
              className="flex items-center gap-2 rounded-md bg-[#0060C7] px-5 py-2 text-white font-medium text-sm hover:bg-[#004ea1] transition-colors ml-2"
            >
              <img src={star} alt="Star Icon" className="w-[14px] h-[14px]" />
              Experience Center
            </a> */}
            <AnimatedButton
              className={"fancy relative h-[40px] w-[210px] px-[10px]"}
            >
              <span className="z-10 flex gap-1 sm:gap-2 items-center justify-center text-white font-montserrat font-medium md:px-0 px-[10px] text-[14px]">
                <img
                  src={star}
                  alt="stars"
                  className="animate-pulse-scale h-4 w-4"
                />
                Experience Center
              </span>
            </AnimatedButton>
          </nav>

          {/* Mobile Navigation */}
          <div className="relative flex md:hidden">
            <button
              ref={buttonRef}
              type="button"
              onClick={() => {
                setIsSidebarOpen((prev) => !prev);
                setIsOfferingsOpen(false);
              }}
            >
              <img
                src={isSidebarOpen ? cross : dropdown}
                alt={isSidebarOpen ? "Close Menu" : "Open Menu"}
                className="h-6 w-6"
              />
            </button>

            {isSidebarOpen && (
              <nav
                className="absolute top-full right-0 bg-white shadow-md w-[250px] flex flex-col rounded-[8px] mt-2 z-50"
                ref={sidebarRef}
              >
                <div className="w-full p-4 flex flex-col space-y-4">
                  <AnimatedButton
                    onClick={() =>
                      window.open(
                        "https://ai.think41.com/login",
                        "_blank",
                        "noopener,noreferrer"
                      )
                    }
                    className={"fancy relative h-[36px] w-full"}
                  >
                    <span className="z-10 flex gap-3 items-center justify-center text-white font-montserrat text-[14px] font-medium">
                      <img
                        src={star}
                        alt="stars"
                        className="animate-pulse-scale h-4 w-4 md:h-5 md:w-5"
                      />
                      Experience Center
                    </span>
                  </AnimatedButton>

                  <Link
                    to="/about"
                    className="relative group text-black font-montserrat"
                  >
                    About us
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-500 transition-all duration-300 group-hover:w-full" />
                  </Link>

                  <div className="relative">
                    <button
                      className="flex items-center justify-between w-full text-black font-montserrat"
                      onClick={() => setIsOfferingsOpen(!isOfferingsOpen)}
                    >
                      Offerings
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-300 ${
                          isOfferingsOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    <div
                      className={`transition-all duration-300 overflow-hidden ${
                        isOfferingsOpen ? "max-h-48 mt-1" : "max-h-0"
                      }`}
                    >
                      <Link
                        to="/offering-a"
                        className="flex items-center justify-between py-2 text-[16px] font-roboto font-[400] text-gray-800 hover:bg-gray-100"
                      >
                        AI Solutions
                        <ArrowRight className="h-4 w-4 text-gray-500" />
                      </Link>
                      <div className="h-[1px] bg-gray-200" />
                      <Link
                        to="/offering-a#ai_platform"
                        className="flex items-center justify-between py-2 text-[16px] font-roboto font-[400] text-gray-800 hover:bg-gray-100"
                      >
                        AI Platforms
                        <ArrowRight className="h-4 w-4 text-gray-500" />
                      </Link>
                      <div className="h-[1px] bg-gray-200" />
                      <Link
                        to="/offering-b#ai_engineering"
                        className="flex items-center justify-between py-2 text-[16px] font-roboto font-[400] text-gray-800 hover:bg-gray-100"
                      >
                        AI Engineering
                        <ArrowRight className="h-4 w-4 text-gray-500" />
                      </Link>
                    </div>
                  </div>

                  <Link
                    to="/careers"
                    className="relative group text-black font-montserrat"
                  >
                    Careers
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-500 transition-all duration-300 group-hover:w-full" />
                  </Link>
                  <Link
                    to="/contact"
                    className="relative group text-black font-montserrat"
                  >
                    Contact Us
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-500 transition-all duration-300 group-hover:w-full" />
                  </Link>
                </div>
              </nav>
            )}
          </div>
        </div>
      </header>
    </div>
  );
}
